import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import {
  useGetAllBlueprints,
  useGetBehaviorKits,
} from "../../api/BlueprintsApi";
import {
  useCompaniesFetchAll,
  useGetCompanyWithBlueprint,
} from "../../api/CompaniesApi";
import { HARDCODED_ACTOR_UUID } from "../../api/helpers";
import {
  buildHybridRerunPersonPayload,
  personInfoApiFromPersonFormState,
} from "../../api/hybridRerunPerson";
import {
  usePostCreateReportV2,
  buildHybridRerunReportBody,
  mergeHybridRerunProfileUrls,
  useRerunReportRequest,
} from "../../api/ReportsApi";
import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../../shared-components/Button";
import Container from "../../shared-components/Container";
import DropdownCombo, {
  DropdownItem,
} from "../../shared-components/DropdownCombo";
import FormDividedColumnContainer from "../../shared-components/FormDividedColumnContainer";
import InputField from "../../shared-components/InputField";
import PageHeader from "../../shared-components/PageHeader";
import LabeledCheckbox from "../../shared-components/LabeledCheckbox";

import PersonFormAddresses, {
  AddressesDefinitions,
} from "./PersonFormAddressesv2";
import PersonFormAliases, { AliasesDefinitions } from "./PersonFormAliasesv2";
import PersonFormEducation, {
  EducationDefinitions,
} from "./PersonFormEducationv2";
import PersonFormEmails, { EmailDefinitions } from "./PersonFormEmailsv2";
import PersonFormErrorSummary from "./PersonFormErrorSummaryv2";
import PersonFormJob, { JobDefinitions } from "./PersonFormJobv2";

import PersonFormPhones, { PhoneDefinitions } from "./PersonFormPhonesv2";
import {
  ActionTypes,
  buildEmptyState,
  personFormReducer,
} from "./PersonFormReducerV2";
import PersonFormSocial, { SocialDefinitions } from "./PersonFormSocialv2";
import { PersonFormStateError } from "../PersonFormErrorState";
import LoadingSpinner from "../../shared-components/LoadingSpinner";
import {
  dropdownItemSortEval,
  formatDateForEndpoint,
  uniqueUUID,
} from "../../utils";
import {
  ChevronLeftIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { BehaviorKit, CompanyWithBlueprintInfo } from "../../api/Companies";
import {
  CheckTypeInputApi,
  CreateReportInputV2Api,
  REPORT_TYPES,
  ReportsPendingReviewItem,
} from "../../api/Reports";
import { Modal } from "../../shared-components/Modal";
import { PersonFormReview } from "./PersonFormReviewv2";
import { useGetPersonInfo, useUploadResume } from "../../api/PersonApi";
import { useFamaFlags } from "../../api/FamaFlags";
import {
  RERUN_PROFILE_PREFILL_PAGE_SIZE,
  useFetchItemsForReview,
} from "../../api/ItemsApi";
import { ItemTypes } from "../../api/Items";
import { usePermissionValidator } from "../../api/UserPermissionValidators";
import { JobTitleItems } from "../../api/JobTitlesApi";

import CheckTypesMulti from "./CheckTypesMulti";
import { BlueprintMinimalInfo } from "../../api/Blueprints";
import { NameType } from "../../api/Person";

const PersonForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as {
    rerun: boolean;
    parent_report: ReportsPendingReviewItem;
  };
  const { data: personInfo, mutate: getPerson } = useGetPersonInfo();
  const [state, dispatch] = useReducer(personFormReducer, buildEmptyState());
  const [showErrors, setShowErrors] = useState(false);
  const { showResumeParse, showBehaviorKit, hybridReportRerun } =
    useFamaFlags();

  const [companyUUID, setCompanyUUID] = useState<string>();
  const [personFormError, setPersonFormError] = useState<PersonFormStateError>(
    {}
  );
  const [showModal, setShowModal] = useState(false);

  const { data: profileList, mutate: getProfiles } = useFetchItemsForReview();

  const initialBluePrintCheck = useRef<unknown>(null);
  const initialJobTitleCheck = useRef<unknown>(null);
  const initialBehaviorKitChange = useRef<unknown>(null);
  const [blueprintItems, setBlueprintItems] = useState<BlueprintMinimalInfo[]>(
    []
  );
  const initialJobTitle = useMemo(() => {
    if (!locationState) {
      return JobTitleItems[0];
    }
    const job = locationState.parent_report
      ? JobTitleItems.find(
          (item) => item.label === locationState.parent_report.job_title
        )
      : null;
    if (job) {
      dispatch({ type: ActionTypes.setJobTitle, payload: job.id });
    }
    return job ?? JobTitleItems[0];
  }, [locationState]);
  const fileInput = useRef<HTMLInputElement>(null);
  const profilesPrefilledFromParentRef = useRef(false);
  const navigateBack = () => {
    navigate("/reports");
  };
  const {
    mutate: getAllCompanies,
    data: companiesInfo,
    isLoading: isLoadingCompanies,
  } = useCompaniesFetchAll();
  const {
    mutate: getBlueprints,
    data: blueprintsList,
    isLoading: isLoadingBlueprints,
  } = useGetAllBlueprints();

  const { mutate: getBehaviorKits, data: behaviorKitList } =
    useGetBehaviorKits();

  useEffect(() => {
    if (locationState?.rerun) {
      if (locationState?.parent_report?.person_uuid) {
        getPerson(locationState?.parent_report?.person_uuid);
        getProfiles({
          report_uuid: locationState?.parent_report.report_uuid,
          item_type_name: [ItemTypes.SOCIAL_MEDIA_PROFILE],
          // Server default page_size is 10 (newest first). Incomplete prefill
          // omits older CLIENT/provided URLs; EI then drops them while recovering
          // manuals — fetch every page so the form is the full parent set.
          page_size: RERUN_PROFILE_PREFILL_PAGE_SIZE,
          fetch_all: true,
        });
      }
      setCompanyUUID(locationState.parent_report.company_uuid);
      dispatch({
        type: ActionTypes.setCompany,
        payload: locationState.parent_report.company_uuid,
      });
      dispatch({
        type: ActionTypes.setCustomID,
        payload: locationState.parent_report.client_report_id,
      });
      const found = blueprintItems.find((blueprint) => {
        return (
          blueprint.blueprint_uuid ===
          locationState?.parent_report?.blueprint_uuid
        );
      });
      if (found) {
        setBlueprintChecks([found]);
        dispatch({
          type: ActionTypes.setBlueprints,
          payload: [found],
        });
      }
      if (showBehaviorKit) {
        dispatch({
          type: ActionTypes.setBehaviorKit,
          payload:
            locationState.parent_report.behavior_kit_uuid &&
            locationState.parent_report.behavior_kit_uuid !== ""
              ? locationState.parent_report.behavior_kit_uuid
              : "NULL",
        });
      }
    }
  }, [getPerson, getProfiles, locationState, blueprintItems, showBehaviorKit]);

  useEffect(() => {
    if (state.company_uuid) {
      setCompanyUUID(state.company_uuid);
    }
  }, [state]);

  useEffect(() => {
    if (!profileList || !locationState?.rerun) {
      return;
    }
    if (profilesPrefilledFromParentRef.current) {
      return;
    }
    profilesPrefilledFromParentRef.current = true;

    const mergedUrls =
      mergeHybridRerunProfileUrls(profileList.items, state.profiles) ?? [];

    const seeded = mergedUrls.map((url) => ({
      url,
      key: uniqueUUID(),
    }));
    setSocialMedia(seeded);
    dispatch({ type: ActionTypes.setProfiles, payload: seeded });
  }, [profileList, locationState?.rerun, dispatch, state.profiles]);

  useEffect(() => {
    if (companyUUID) {
      getBehaviorKits(companyUUID);
    }
  }, [getBehaviorKits, companyUUID]);

  useEffect(() => {
    if (personInfo) {
      if (!personInfo.names?.[0]) return;
      setAliases(
        personInfo.names.map((name) => ({
          firstName: name.first_name,
          middleName: name.middle_name ?? "",
          lastName: name.last_name,
          key: uniqueUUID(),
          name_type: name.name_type,
        }))
      );

      setEmails(
        personInfo.emails.map((email) => ({
          email: email.address,
          key: uniqueUUID(),
        }))
      );
      setPhones(
        personInfo.phones.map((phone) => ({
          phone: phone.raw_number,
          key: uniqueUUID(),
        }))
      );
      setAddresses(
        personInfo.addresses.map((address) => ({
          street: (address.line1 || "").replace(/[,.;-]/g, ""),
          city: address.city || "",
          state: address.state || "",
          zip: address.zip || "",
          country: address.country || "",
          key: uniqueUUID(),
        }))
      );
      setEducation(
        personInfo.degrees.map((education) => ({
          degree: education.name.replace(/[,.-]/g, ""),
          institution: education.educator.replace(/[,.-]/g, ""),
          startDate: education.start_date,
          endDate: education.end_date,
          country: education.country ? education.country : "",
          key: uniqueUUID(),
        }))
      );
      setJobs(
        personInfo.jobs.map((job) => ({
          title: (job.title || "").replace(/[,.;-]/g, ""),
          company: (job.employer || "").replace(/[,.;-]/g, ""),
          startDate: job.start_date || "",
          endDate: job.end_date || "",
          country: job.country,
          key: uniqueUUID(),
        }))
      );
      setBirthDate(personInfo.birth_date ?? "");
    }
  }, [personInfo]);

  const {
    mutate: getCompaniesInfoWithBlueprint,
    data: companiesWithBlueprint,
    isLoading: isLoadingCompanyBlueprints,
  } = useGetCompanyWithBlueprint();
  const [companyItems, setCompanyItems] = useState<DropdownItem[]>([]);

  const [behaviorKitItems, setBehaviorKitItems] = useState<DropdownItem[]>([]);
  const [birthDate, setBirthDate] = useState<string>("");
  const {
    mutateAsync: createPerson,
    isLoading: isLoadingCreatePerson,
    error: errorCreatePerson,
    errorData: errorCreatePersonData,
  } = usePostCreateReportV2();
  const {
    mutateAsync: rerunReportRequest,
    isLoading: isLoadingRerun,
    error: errorRerun,
  } = useRerunReportRequest();
  const { canBulkUploadReports, canInternalQaView } = usePermissionValidator();

  const isWaitingForParentProfiles =
    hybridReportRerun && locationState?.rerun === true && profileList == null;

  const {
    mutate: uploadResume,
    data: resumeData,
    isLoading: isLoadingResume,
  } = useUploadResume();
  const [aliases, setAliases] = useState<AliasesDefinitions[]>([]);
  const [jobs, setJobs] = useState<JobDefinitions[]>([]);
  const [emails, setEmails] = useState<EmailDefinitions[]>([]);
  const [phones, setPhones] = useState<PhoneDefinitions[]>([]);
  const [adddresses, setAddresses] = useState<AddressesDefinitions[]>([]);
  const [education, setEducation] = useState<EducationDefinitions[]>([]);
  const [socialMedia, setSocialMedia] = useState<SocialDefinitions[]>([]);

  const changeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target as HTMLInputElement;
    if (files && files.length > 0) {
      uploadResume(files[0]);
    }
  };
  useEffect(() => {
    if (resumeData) {
      setAliases([
        {
          firstName: resumeData.name.first,
          middleName: resumeData.name.middle,
          lastName: resumeData.name.last,
          key: uniqueUUID(),
          name_type: NameType.MAIN,
        },
      ]);
      setJobs(
        resumeData.jobs.map((job) => ({
          title: (job.job_title || "").replace(/[,.;-]/g, ""),
          company: (job.organization || "").replace(/[,.;-]/g, ""),
          startDate: formatDateForEndpoint(job.start_date) || "",
          endDate: formatDateForEndpoint(job.end_date) || "",
          country: "",
          key: uniqueUUID(),
        }))
      );
      setAddresses(
        resumeData.addresses.map((address) => ({
          street: (address.formatted || "").replace(/[,.;-]/g, ""),
          city: address.city,
          state: address.state,
          zip: "",
          country: address.country,
          key: uniqueUUID(),
        }))
      );
      setEmails(
        resumeData.emails.map((email) => ({
          email: email,
          key: uniqueUUID(),
        }))
      );

      setEducation(
        resumeData.education.map((education) => ({
          degree: (education.accreditation.input_str || "").replace(
            /[,.-]/g,
            ""
          ),
          institution: (education.organization || "").replace(/[,.-]/g, ""),
          startDate: "",
          endDate: formatDateForEndpoint(education.completion_date) || "",
          country: education.location ? education.location.country : "",
          key: uniqueUUID(),
        }))
      );

      setSocialMedia(
        resumeData.websites.map((site) => ({ url: site, key: uniqueUUID() }))
      );
    }
  }, [resumeData]);

  useEffect(() => {
    getAllCompanies();
    getBlueprints();
  }, [getAllCompanies, getBlueprints]);

  const [blueprintChecks, setBlueprintChecks] = useState<
    BlueprintMinimalInfo[]
  >([]);

  const onQaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.id === "client_qa") {
      dispatch({
        type: ActionTypes.setClientQa,
        payload: e.target.checked,
      });
    } else if (e.target.id === "internal_qa") {
      dispatch({
        type: ActionTypes.setInternalQa,
        payload: e.target.checked,
      });
    }
  };

  const onSourceChange = (active: boolean, bp_uuid: string) => {
    // always remove requested item first to prevent duplicates
    let newSources = blueprintChecks.filter((source) => {
      return source.blueprint_uuid !== bp_uuid;
    });
    if (active) {
      const found = blueprintItems.find((blueprint) => {
        return blueprint.blueprint_uuid === bp_uuid;
      });
      if (found) {
        // remove other checks of same type as found
        newSources = newSources.filter(
          (item) => item.report_type !== found.report_type
        );
        newSources.push(found);
      }
    }
    setBlueprintChecks(newSources);
    dispatch({
      type: ActionTypes.setBlueprints,
      payload: newSources,
    });
  };

  useEffect(() => {
    if (companiesInfo && companiesInfo.length > 0) {
      const list = companiesInfo;
      const items: DropdownItem[] = list.map((element) => {
        return { id: element.external_id, label: element.name };
      });
      setCompanyItems(items);
      if (locationState?.rerun) {
        setCompanyItems([
          {
            id: locationState.parent_report.company_uuid,
            label: locationState.parent_report.company_name,
          },
        ]);
      }
    }

    let company: CompanyWithBlueprintInfo | null = null;
    if (companiesWithBlueprint && companiesWithBlueprint.length > 0) {
      const list = companiesWithBlueprint;
      const items: BlueprintMinimalInfo[] = (
        list?.[0]?.blueprintsInfo ?? []
      ).filter(
        (blueprint) => blueprint.report_type !== REPORT_TYPES.INSTANTFIT
      );
      setBlueprintItems(items);

      if (companiesWithBlueprint && companiesWithBlueprint.length > 0) {
        company = companiesWithBlueprint[0];
        dispatch({
          type: ActionTypes.setClientQa,
          payload: company.client_qa ? true : false,
        });
        dispatch({
          type: ActionTypes.setInternalQa,
          payload: company.internal_qa ? true : false,
        });
      }
    }

    setBehaviorKitItems(createBehaviorsKitItems(company, behaviorKitList));
    return () => {
      initialBluePrintCheck.current = null;
      initialBehaviorKitChange.current = null;
    };
  }, [
    companiesInfo,
    blueprintsList,
    companiesWithBlueprint,
    behaviorKitList,
    locationState,
  ]);

  useEffect(() => {
    dispatch({
      type: ActionTypes.setBirthdate,
      payload: birthDate,
    });
  }, [birthDate]);

  function close() {
    setShowModal(false);
  }

  const handleCompanyChange = useCallback(
    (item: DropdownItem) => {
      if (!blueprintsList) return;
      getCompaniesInfoWithBlueprint({
        company_uuids: [item.id],
        blueprintsList: blueprintsList,
      });
      setCompanyUUID(item.id);
      dispatch({ type: ActionTypes.setCompany, payload: item.id });
      if (companiesWithBlueprint && companiesWithBlueprint.length > 0) {
        dispatch({
          type: ActionTypes.setClientQa,
          payload: companiesWithBlueprint[0].client_qa ? true : false,
        });
        dispatch({
          type: ActionTypes.setInternalQa,
          payload: companiesWithBlueprint[0].internal_qa ? true : false,
        });
      }
      const newSources: BlueprintMinimalInfo[] = [];
      setBlueprintChecks(newSources);
      dispatch({ type: ActionTypes.setBlueprints, payload: newSources });
    },
    [blueprintsList, getCompaniesInfoWithBlueprint, companiesWithBlueprint]
  );

  const handleBehaviorKitChange = (item: DropdownItem) => {
    initialBehaviorKitChange.current = false;
    dispatch({ type: ActionTypes.setBehaviorKit, payload: item.id });
  };

  const createBehaviorsKitItems = (
    company: CompanyWithBlueprintInfo | null,
    behaviorKList: BehaviorKit[] | undefined
  ) => {
    if (behaviorKList && behaviorKList.length > 0) {
      const list = behaviorKList;
      const items: DropdownItem[] = list.map((element) => {
        return {
          id: element.external_id ?? "",
          label: element.name,
        };
      });
      items.push({ id: "NULL", label: "General" });
      items.sort(dropdownItemSortEval);

      let default_bk = "null";
      if (company?.default_behavior_kit) {
        default_bk = company?.default_behavior_kit.toLowerCase();
      }
      const sort_by_default_bk = (a: DropdownItem, b: DropdownItem) => {
        if (a.id.toLowerCase() === default_bk) {
          return -1;
        }
        return 0;
      };
      items.sort(sort_by_default_bk);
      return items;
    } else {
      const emptyList: DropdownItem[] = [{ id: "NULL", label: "General" }];
      return emptyList;
    }
  };

  const showConfirmation = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    setShowErrors(true);
    Object.keys(personFormError).length === 0 && setShowModal(true);
  };
  const handleSubmit = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    setShowErrors(true);
    // TODO: Improve error handling so the add person button is disabled
    // if there are errors, and the endpoint call doesn't happen

    if (Object.keys(personFormError).length !== 0) {
      return;
    }

    // Hybrid PATCH supports one check type; multi-select reruns use createPerson below.
    if (
      hybridReportRerun &&
      locationState?.rerun === true &&
      locationState?.parent_report?.report_uuid &&
      state.blueprint_uuids.length === 1
    ) {
      const selectedCheck = state.blueprint_uuids[0];
      const blueprintUuid =
        selectedCheck.blueprint_uuid ||
        locationState.parent_report.blueprint_uuid;
      const includeBehaviorKitInPayload =
        showBehaviorKit &&
        selectedCheck.report_type === REPORT_TYPES.MISCONDUCT;

      // Send form URLs (incl. newly added). EI re-merges all parent in_report URLs
      // so omissions cannot drop provided/manual profiles from the child.
      const profileUrls = profilesPrefilledFromParentRef.current
        ? state.profiles
        : undefined;

      const currentPerson = personInfoApiFromPersonFormState(state, {
        birthDate,
        aliases,
        jobs,
        emails,
        phones,
        addresses: adddresses,
        education,
      });
      const person = buildHybridRerunPersonPayload({
        current: currentPerson,
        modifiedBy: HARDCODED_ACTOR_UUID,
        personUuid: state.person_uuid || undefined,
        parentPersonUuid: locationState?.parent_report?.person_uuid,
      });

      rerunReportRequest({
        reportUUID: locationState.parent_report.report_uuid,
        body: buildHybridRerunReportBody({
          blueprintUuid,
          behaviorKitUuid: state.behavior_kit_uuid,
          includeBehaviorKitInPayload,
          profileUrls,
          person,
          customId: state.custom_id,
        }),
      })
        .then(() => {
          navigate("/reports", { state: { reportSubmitted: true } });
        })
        .catch(() => {
          // error surfaced via errorRerun -> errorMessage below
        });
      return;
    }

    let checkTypes: CheckTypeInputApi[] = [];
    state.blueprint_uuids.forEach((check) => {
      let checkType: CheckTypeInputApi = {
        blueprint_uuid: check.blueprint_uuid,
        custom_fields: {
          custom_id: state.custom_id,
          job_title: state.job_title ?? "None",
        },
      };

      if (check.report_type === REPORT_TYPES.MISCONDUCT) {
        checkType.custom_fields = {
          ...checkType.custom_fields,
          behavior_kit: state.behavior_kit_uuid,
        };
        if (
          state.behavior_kit_uuid === "NULL" ||
          state.behavior_kit_uuid === ""
        ) {
          delete checkType.custom_fields.behavior_kit;
        }
      }
      checkTypes.push(checkType);
    });

    const createPersonPayload: CreateReportInputV2Api = {
      person_info: {
        external_id: state.person_uuid,
        names: state.names.map((name) => {
          return {
            first_name: name.first_name.trim(),
            last_name: name.last_name.trim(),
            middle_name: name.middle_name.trim(),
            name_type: name.name_type,
          };
        }),
        jobs: state.jobs,
        addresses: state.addresses,
        birth_date: formatDateForEndpoint(state.birth_date),
        degrees: state.degrees,
        phones: state.phones.map((p) => ({
          raw_number: p.raw_number.replace(/[^0-9]/g, ""),
        })),
        emails: state.emails,
        // WARNING!!! TD-1: remove this property once the Authorization cicle gets completed
        modified_by: HARDCODED_ACTOR_UUID,
      },
      profile_urls: state.profiles
        .map((profile) => profile.trim())
        .filter((profile) => profile.length > 0),
      company_uuid: state.company_uuid,
      rerun_from_parent_uuid: locationState?.parent_report?.report_uuid ?? "",
      check_types: checkTypes,
      client_qa: state.client_qa,
      internal_qa: state.internal_qa,
    };
    if (!createPersonPayload.person_info.external_id) {
      delete createPersonPayload.person_info.external_id;
    }
    if (!locationState?.parent_report?.report_uuid) {
      delete createPersonPayload.rerun_from_parent_uuid;
    }
    createPerson(createPersonPayload).then((resultCreatePerson) => {
      if (resultCreatePerson) {
        navigate("/reports", { state: { reportSubmitted: true } });
        // the state: reportSubmitted is for the toast message in the reports lists...
      }
    });
  };

  let errorMessage = errorCreatePerson
    ? [
        `Couldn't save the report please check the field information and try again.`,
        ...errorCreatePersonData,
      ].map((p) => <div>{p}</div>)
    : errorRerun
    ? [`Couldn't rerun the report. Please try again or contact support.`].map(
        (p) => <div>{p}</div>
      )
    : "";

  function hasReportTypeChecked(reportType: REPORT_TYPES) {
    return blueprintChecks.some((check) => {
      return blueprintItems.some((blueprint) => {
        return (
          blueprint.blueprint_uuid === check.blueprint_uuid &&
          blueprint.report_type === reportType
        );
      });
    });
  }

  function onJobTitleChange(item: DropdownItem): void {
    initialJobTitleCheck.current = false;
    dispatch({ type: ActionTypes.setJobTitle, payload: item.id });
  }

  return (
    <div className="-mx-6">
      <div className="px-24">
        <div
          className="flex gap-3 text-xs font-thin text-gray-700 mb-4 items-center cursor-pointer"
          onClick={navigateBack}
        >
          <ChevronLeftIcon className="w-3" />
          <span>Back to Reports</span>
        </div>

        <PageHeader
          title={locationState?.parent_report ? "Rerun Report" : "Add Report"}
        >
          <div className="flex gap-4 items-center">
            {canBulkUploadReports && (
              <Link className="text-blue-400 text-sm" to="/reports/bulkupload">
                Bulk Upload reports
              </Link>
            )}
            {showResumeParse && (
              <Button
                disabled={isLoadingResume}
                type={ButtonTypes.default}
                text={isLoadingResume ? "Parsing..." : "Parse Resume"}
                colorType={ButtonColorTypes.alternate}
                onClick={() =>
                  fileInput && fileInput.current && fileInput.current.click()
                }
              />
            )}
          </div>
        </PageHeader>
        <Container>
          <form>
            <FormDividedColumnContainer title="SCREENING COMPANY">
              <div className="flex gap-4">
                <div className="flex-1 items-center">
                  <DropdownCombo
                    items={companyItems}
                    initial={locationState?.rerun ? companyItems[0] : undefined}
                    disabled={isLoadingCompanies || locationState?.rerun}
                    onChange={handleCompanyChange}
                    needTypeAhead={!locationState?.rerun}
                    label={"Company*"}
                    placeHolder={
                      isLoadingCompanies ? "Loading" : "Select Company"
                    }
                    containerClassName="w-full"
                    buttonContainerClassName="w-full"
                    name="selected-company"
                    {...(showErrors && {
                      isValid: personFormError.company ? false : true,
                    })}
                  ></DropdownCombo>
                </div>
                <div className="flex-1">
                  <DropdownCombo
                    containerClassName=""
                    items={JobTitleItems}
                    placeHolder={"Select Job Title"}
                    onChange={onJobTitleChange}
                    label={"Job Title"}
                    {...(initialJobTitleCheck.current === null &&
                      initialJobTitle && {
                        initial: initialJobTitle,
                      })}
                    name="selected-job-title"
                    needTypeAhead={true}
                    buttonContainerClassName="w-full"
                    icon={
                      <InformationCircleIcon
                        height={24}
                        width={24}
                        color="grey"
                      />
                    }
                    tooltip="Enter the job title for the position the candidate is applying for to enhance your searchability and reporting experience over time."
                  ></DropdownCombo>
                </div>
              </div>
            </FormDividedColumnContainer>
            {/* Blueprint Section */}
            <FormDividedColumnContainer title="CHECK TYPE" className="mt-12">
              <div className="flex">
                <div className="flex flex-col flex-1 pr-4 h-full">
                  {(isLoadingBlueprints || isLoadingCompanyBlueprints) && (
                    <LoadingSpinner />
                  )}
                  {!(isLoadingBlueprints || isLoadingCompanyBlueprints) &&
                    blueprintItems.length === 0 &&
                    "Please select a company first"}
                  {blueprintItems.length !== 0 && (
                    <div className="flex-1">
                      <span className="block mb-2 text-sm font-medium text-gray-600">
                        Check Type*
                      </span>
                      <div className="bg-white p-4 border border-gray-300 rounded-lg h-full flex-1">
                        <CheckTypesMulti
                          blueprintItems={blueprintItems}
                          selections={blueprintChecks}
                          onChange={onSourceChange}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  {behaviorKitItems &&
                    showBehaviorKit &&
                    hasReportTypeChecked(REPORT_TYPES.MISCONDUCT) && (
                      <DropdownCombo
                        containerClassName="mt-5"
                        items={behaviorKitItems}
                        {...(initialBehaviorKitChange.current === null && {
                          initial:
                            locationState?.rerun &&
                            locationState.parent_report.behavior_kit_uuid &&
                            locationState.parent_report.behavior_kit_name
                              ? {
                                  id: locationState.parent_report
                                    .behavior_kit_uuid,
                                  label:
                                    locationState.parent_report
                                      .behavior_kit_name,
                                }
                              : behaviorKitItems[0],
                        })}
                        onChange={handleBehaviorKitChange}
                        label={"Select Behavior Kit*"}
                        name="selected-behavior-kit"
                        icon={
                          <InformationCircleIcon
                            height={24}
                            width={24}
                            color="grey"
                          />
                        }
                        tooltip="Behavior kits are the configurable filters that companies can use to set behavior flags, lookback periods, and keywords for any given check type.
                    The General Behavior Kit utilizes configured settings derived from the selected Check Type."
                      ></DropdownCombo>
                    )}
                  {hasReportTypeChecked(REPORT_TYPES.MISCONDUCT) &&
                    companiesWithBlueprint &&
                    companiesWithBlueprint.length > 0 &&
                    (companiesWithBlueprint[0]?.client_qa ||
                      companiesWithBlueprint[0]?.internal_qa) && (
                      <div className="flex-1 mt-2">
                        <span className="block mb-2 text-sm font-medium text-gray-600">
                          Investigator
                        </span>
                        <div className="bg-white p-4 border border-gray-300 rounded-lg h-full flex-1">
                          {companiesWithBlueprint[0]?.internal_qa &&
                            canInternalQaView && (
                              <LabeledCheckbox
                                key="internal_qa"
                                id="internal_qa"
                                label="Internal Investigator"
                                name="Internal Investigator"
                                preserveLabelCasing={true}
                                onChange={onQaChange}
                                disabled={false}
                                checked={state.internal_qa}
                              ></LabeledCheckbox>
                            )}
                          {companiesWithBlueprint[0]?.client_qa && (
                            <LabeledCheckbox
                              key="client_qa"
                              id="client_qa"
                              label="Client Investigator"
                              name="Client Investigator"
                              onChange={onQaChange}
                              disabled={false}
                              checked={state.client_qa}
                            ></LabeledCheckbox>
                          )}
                        </div>
                      </div>
                    )}
                </div>
              </div>
            </FormDividedColumnContainer>
            {/* Alias Section */}
            <FormDividedColumnContainer
              title="PERSONAL INFORMATION"
              className="mt-12"
            >
              <PersonFormAliases
                {...(aliases && { prefill: aliases })}
                onChange={(aliases) =>
                  dispatch({ type: ActionTypes.setNames, payload: aliases })
                }
                alternateColor={true}
                {...(showErrors && { error: personFormError })}
              />
            </FormDividedColumnContainer>
            {/* Emails Section */}
            <FormDividedColumnContainer className="">
              <div className="flex gap-4">
                <div className="flex-1">
                  <PersonFormEmails
                    {...(emails && { prefill: emails })}
                    onChange={(emails) =>
                      dispatch({ type: ActionTypes.setEmails, payload: emails })
                    }
                    {...(showErrors && { error: personFormError })}
                    alternateColor={true}
                  />
                </div>
                <div className="flex-1">
                  <PersonFormPhones
                    {...(phones && { prefill: phones })}
                    onChange={(phones) =>
                      dispatch({ type: ActionTypes.setPhones, payload: phones })
                    }
                    {...(showErrors && { error: personFormError })}
                    alternateColor={true}
                  />
                </div>
              </div>
            </FormDividedColumnContainer>
            {/* BirthDate Section */}
            <FormDividedColumnContainer className="">
              <InputField
                value={birthDate}
                handleChange={(e) => {
                  setBirthDate(e.target.value);
                }}
                name={"birth-date"}
                placeholder=""
                label={"Birthday"}
                type="date"
                max={new Date().toISOString().split("T")[0]}
                {...(showErrors && {
                  className: personFormError?.birth_date
                    ? "border-rose-600"
                    : "",
                })}
              />
            </FormDividedColumnContainer>

            {/* Social Networks Section */}
            <FormDividedColumnContainer
              title="SOCIAL NETWORKS"
              className="mt-12"
            >
              <PersonFormSocial
                {...(socialMedia && { prefill: socialMedia })}
                onChange={(social) =>
                  dispatch({ type: ActionTypes.setProfiles, payload: social })
                }
                {...(showErrors && { error: personFormError })}
                alternateColor={true}
              />
            </FormDividedColumnContainer>
            {/* Address Section */}
            <FormDividedColumnContainer title="ADDRESS" className="mt-12">
              <PersonFormAddresses
                {...(adddresses && { prefill: adddresses })}
                onChange={(addresses) =>
                  dispatch({
                    type: ActionTypes.setAddresses,
                    payload: addresses,
                  })
                }
                {...(showErrors && { error: personFormError })}
                alternateColor={true}
              />
            </FormDividedColumnContainer>
            {/* Job Section */}
            <FormDividedColumnContainer
              title="OTHER CANDIDATE INFORMATION"
              className="mt-12"
            >
              <div className="">
                <span className="block font-semibold mb-3 text-gray-800">
                  Employment
                </span>
                <PersonFormJob
                  {...(jobs && { prefill: jobs })}
                  onChange={(jobs) =>
                    dispatch({ type: ActionTypes.setJobs, payload: jobs })
                  }
                  {...(showErrors && { error: personFormError })}
                  alternateColor={true}
                />
              </div>
              <div className="mt-4">
                <span className="block font-semibold mb-3 text-gray-800">
                  Education
                </span>
                <PersonFormEducation
                  {...(education && { prefill: education })}
                  onChange={(education) =>
                    dispatch({
                      type: ActionTypes.setDegrees,
                      payload: education,
                    })
                  }
                  {...(showErrors && { error: personFormError })}
                  alternateColor={true}
                />
              </div>
            </FormDividedColumnContainer>

            {/* Client Reference */}
            <FormDividedColumnContainer
              title="CLIENT REPORT ID"
              className="mt-12"
            >
              <InputField
                handleChange={(e) => {
                  dispatch({
                    type: ActionTypes.setCustomID,
                    payload: e.target.value,
                  });
                }}
                label="Client Report ID"
                tooltip="Enter the Client Report ID that corresponds to this candidate's overall background check report. This ensures the Fama report can be easily matched and integrated with the candidate's background check report. This is best used for CRAs."
                name={"reference-id"}
                placeholder="ID number"
                defaultValue={
                  locationState?.rerun
                    ? locationState?.parent_report?.client_report_id
                    : ""
                }
              />
            </FormDividedColumnContainer>

            <PersonFormErrorSummary
              formState={state}
              handleError={setPersonFormError}
              showErrors={showErrors}
            />

            <div className="mt-6">
              <Button
                disabled={
                  isLoadingCreatePerson ||
                  isLoadingRerun ||
                  isWaitingForParentProfiles
                }
                text={
                  isLoadingCreatePerson || isLoadingRerun ? (
                    <LoadingSpinner />
                  ) : (
                    "Add Report"
                  )
                }
                colorType={ButtonColorTypes.custom}
                className="bg-orange-500 text-white rounded-full"
                onClick={showConfirmation}
              />
            </div>
            <input
              className="hidden"
              type="file"
              ref={fileInput}
              name="_file"
              onChange={changeHandler}
            />
          </form>
        </Container>

        <Modal open={showModal} width="summary--customSize" onClose={close}>
          <PersonFormReview
            isLoadingCreatePerson={isLoadingCreatePerson || isLoadingRerun}
            close={close}
            behaviorKitItems={behaviorKitItems}
            companyItems={companyItems}
            blueprintItems={blueprintChecks}
            state={state}
            handleSubmit={handleSubmit}
            error={errorMessage}
          />
        </Modal>
      </div>
    </div>
  );
};

export default PersonForm;
